/**
 * Cycle Selector Page
 * 
 * First screen after login. Shows all cycles operator is assigned to.
 * Route: /mobile/cycle-selector
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { useAuthUser } from '@/hooks/useAuthUser';
import { apiFetch } from '@/lib/api';
import { MobileScaffold } from '@/components/mobile/MobileScaffold';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Calendar, Store, CheckCircle2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface CycleSummary {
  cycleId: number;
  cycleName: string;
  retailerName: string;
  period: string;
  startDate: string;
  endDate: string;
  totalStores: number;
  completedStores: number;
  signedStores: number;
  status: 'active' | 'completed' | 'signed_off';
}

export default function CycleSelector() {
  const [, setLocation] = useLocation();
  const { user } = useAuthUser();
  const { toast } = useToast();

  const operatorId = user?.id ?? (user as any)?.userId ?? (user as any)?.localId;

  // Fetch cycles assigned to operator
  const {
    data: cycles = [],
    isLoading,
    error,
  } = useQuery<CycleSummary[]>({
    queryKey: ['operatorCycles', operatorId],
    queryFn: async () => {
      if (!operatorId) {
        throw new Error('Operator ID not found');
      }

      const response = await apiFetch(`/api/mobile/operator-cycles`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to fetch cycles: ${response.status}`);
      }

      return response.json();
    },
    enabled: !!operatorId,
    staleTime: 30_000, // 30 seconds
  });

  // Cycle selection mutation
  const selectCycleMutation = useMutation({
    mutationFn: async (cycleId: number) => {
      const response = await apiFetch(`/api/mobile/cycle-select/${cycleId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ operatorId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to select cycle: ${response.status}`);
      }

      return response.json();
    },
    onSuccess: (data, cycleId) => {
      toast({
        title: 'Cycle selected',
        description: `Starting work on ${data.cycleName}`,
      });
      // Navigate to route overview with cycle ID
      setLocation(`/mobile/route-overview/${operatorId}/${cycleId}`);
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to select cycle',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  if (isLoading) {
    return (
      <MobileScaffold title="Select Cycle">
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-32 mt-2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </MobileScaffold>
    );
  }

  if (error) {
    return (
      <MobileScaffold title="Select Cycle">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <p className="text-red-600 mb-4">
              {(error as Error).message || 'Failed to load cycles'}
            </p>
            <Button onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        </div>
      </MobileScaffold>
    );
  }

  if (cycles.length === 0) {
    return (
      <MobileScaffold title="Select Cycle">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Cycles Assigned</h2>
            <p className="text-gray-600">
              You don't have any cycles assigned yet. Contact your administrator.
            </p>
          </div>
        </div>
      </MobileScaffold>
    );
  }

  return (
    <MobileScaffold title="Select Cycle">
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {cycles.map((cycle) => {
          const progressPercentage = cycle.totalStores > 0 
            ? Math.round((cycle.signedStores / cycle.totalStores) * 100)
            : 0;

          const statusConfig = {
            active: {
              label: 'Active',
              variant: 'default' as const,
              color: 'bg-blue-100 text-blue-700',
            },
            completed: {
              label: 'In Progress',
              variant: 'secondary' as const,
              color: 'bg-yellow-100 text-yellow-700',
            },
            signed_off: {
              label: 'Completed',
              variant: 'outline' as const,
              color: 'bg-green-100 text-green-700',
            },
          };

          const config = statusConfig[cycle.status];

          return (
            <Card key={cycle.cycleId} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg mb-1">{cycle.cycleName}</CardTitle>
                    <p className="text-sm text-gray-600">{cycle.retailerName}</p>
                  </div>
                  <Badge className={cn('ml-2', config.color)}>
                    {config.label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Period */}
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar className="h-4 w-4 mr-2" />
                  <span>{cycle.period}</span>
                </div>

                {/* Progress */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Progress</span>
                    <span className="font-medium">
                      {cycle.signedStores} / {cycle.totalStores} stores
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={cn(
                        'h-2 rounded-full transition-all',
                        cycle.status === 'signed_off' 
                          ? 'bg-green-600' 
                          : progressPercentage > 0 
                            ? 'bg-blue-600' 
                            : 'bg-gray-300'
                      )}
                      style={{ width: `${progressPercentage}%` }}
                    />
                  </div>
                </div>

                {/* Enter Cycle Button */}
                <Button
                  onClick={() => selectCycleMutation.mutate(cycle.cycleId)}
                  disabled={selectCycleMutation.isPending || cycle.status === 'signed_off'}
                  className="w-full"
                  variant={cycle.status === 'signed_off' ? 'outline' : 'default'}
                >
                  {selectCycleMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Entering...
                    </>
                  ) : cycle.status === 'signed_off' ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Cycle Complete
                    </>
                  ) : (
                    <>
                      <Store className="h-4 w-4 mr-2" />
                      Enter Cycle
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </MobileScaffold>
  );
}

